import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mentorshipApi, showError, showSuccess } from "../lib";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft,
  Star,
  Calendar,
  Clock,
  Video,
  MessageCircle,
  Check,
  Loader,
  Linkedin,
  Twitter,
  Globe,
  Award,
  Users,
} from "lucide-react";

// Facebook icon component
const Facebook = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

interface Mentor {
  _id: string;
  user: string;
  firstName?: string;
  lastName?: string;
  name: string;
  email?: string;
  title: string;
  company: string;
  companyColor?: string;
  bio: string;
  expertise: string[];
  pricePerSession: number;
  rating?: number;
  reviewsCount?: number;
  totalSessions: number;
  totalStudents: number;
  isVerified: boolean;
  isTopMentor: boolean;
  verified?: boolean;
  topMentor?: boolean;
  avatar?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  facebook?: string;
  hourlyRate?: number;
  yearsOfExperience?: number;
  languages?: string[];
  responseTime?: string;
  availability?: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }[];
}

interface Review {
  _id: string;
  student: {
    name: string;
    avatar?: string;
  };
  rating: number;
  comment: string;
  createdAt: string;
}

const MentorDetailPage = () => {
  const { mentorId } = useParams<{ mentorId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [mentor, setMentor] = useState<Mentor | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    scheduledAt: "",
    topic: "",
    notes: "",
  });

  useEffect(() => {
    if (mentorId) fetchMentor();
  }, [mentorId]);

  const fetchMentor = async () => {
    try {
      setLoading(true);
      
      // Check if this is the logged-in mentor viewing their own profile
      const isOwnProfile = user && mentorId === user._id;
      
      let mentorRes;
      if (isOwnProfile) {
        // Use the by-user endpoint for own profile
        mentorRes = await mentorshipApi.getMentorByUserId(mentorId!);
      } else {
        // Use the regular by-mentor-id endpoint for viewing other mentors
        mentorRes = await mentorshipApi.getMentorById(mentorId!);
      }
      
      const reviewsRes = await mentorshipApi.getMentorReviews(mentorId!, { limit: 10 });
      
      if (mentorRes.success) setMentor(mentorRes.data);
      if (reviewsRes.success) {
        const reviewsWithUser = reviewsRes.data.map((r: any) => ({
          ...r,
          student: r.student?._id ? {
            _id: r.student._id,
            name: r.student.firstName + " " + r.student.lastName,
            avatar: r.student.avatar,
          } : r.student,
        }));
        setReviews(reviewsWithUser);
      }
    } catch (err) {
      console.error("Failed to fetch mentor:", err);
      showError("Failed to load mentor");
    } finally {
      setLoading(false);
    }
  };

  const handleBookSession = async () => {
    if (!bookingForm.scheduledAt || !bookingForm.topic) {
      showError("Please fill in all required fields");
      return;
    }
    try {
      setBooking(true);
      const response = await mentorshipApi.bookSession(
        mentorId!,
        bookingForm.scheduledAt,
        bookingForm.topic,
        bookingForm.notes
      );
      if (response.success) {
        // All sessions (free or paid) need mentor approval
        showSuccess("Session booked successfully! Waiting for mentor approval.");
        setShowBooking(false);
        navigate("/dashboard/student/mentorship-sessions");
      }
    } catch (err: any) {
      console.error("Failed to book session:", err);
      showError(err.message || "Failed to book session");
    } finally {
      setBooking(false);
    }
  };

  const fmtXAF = (amount: number) =>
    new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: "XAF",
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <main className="min-h-screen pt-16 pb-16 bg-gray-50">
        <div className="flex items-center justify-center h-96">
          <Loader className="w-8 h-8 text-primary animate-spin" />
        </div>
      </main>
    );
  }

  if (!mentor) {
    return (
      <main className="min-h-screen pt-16 pb-16 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl">
          <div className="py-12 text-center bg-white border rounded-xl">
            <p className="text-gray-500">Mentor not found</p>
            <button onClick={() => navigate("/mentorship")} className="mt-4 text-primary hover:underline">Go back</button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-16 pb-16 bg-gray-50">
      <div className="max-w-5xl px-2 mx-auto sm:px-4 lg:px-6">
        {/* Header */}
        <div className="flex items-center gap-3 py-4">
          <button
            onClick={() => navigate("/mentorship")}
            className="flex items-center justify-center w-8 h-8 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="space-y-6 lg:col-span-2">
            {/* Profile Card */}
            <div className="p-6 bg-white border border-gray-200 rounded-2xl">
              <div className="flex flex-col gap-6 sm:flex-row">
                {/* Avatar */}
                <div className="relative self-center sm:self-start">
                  <div className="w-32 h-32 overflow-hidden shadow-lg rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 ring-4 ring-gray-100">
                    {mentor.avatar ? (
                      <img src={mentor.avatar} alt={mentor.name} className="object-cover w-full h-full" />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full text-4xl font-bold text-primary">
                        {(mentor.name || "M").charAt(0)}
                      </div>
                    )}
                  </div>
                  {/* Rating badge */}
                  {mentor.rating > 0 && (
                    <div className="absolute flex items-center gap-1 px-2 py-1 text-xs font-bold text-white rounded-full shadow-md -bottom-2 -right-2 bg-amber-500">
                      <Star className="w-3 h-3 fill-white" />
                      {mentor.rating.toFixed(1)}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold text-gray-900">{mentor.name}</h1>
                    {mentor.isVerified && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        <Check className="w-2.5 h-2.5" /> Verified
                      </span>
                    )}
                    {mentor.isTopMentor && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                        <Star className="w-2.5 h-2.5 fill-amber-600" /> Top Mentor
                      </span>
                    )}
                  </div>
                  <p className="mb-2 text-sm text-gray-500">{mentor.title} {mentor.company && `at ${mentor.company}`}</p>
                  
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{mentor.studentsCount || mentor.totalStudents || 0} students</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{mentor.sessionsCount || mentor.totalSessions || 0} sessions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-gray-600">{mentor.reviewsCount || 0} reviews</span>
                    </div>
                  </div>

                  {/* Social Links */}
                  {(mentor.linkedin || mentor.twitter || mentor.website || mentor.facebook) && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                      {mentor.linkedin && (
                        <a 
                          href={mentor.linkedin.startsWith('http') ? mentor.linkedin : `https://linkedin.com/in/${mentor.linkedin}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                        >
                          <Linkedin className="w-3.5 h-3.5 text-blue-600" />
                          <span className="font-medium hidden sm:inline">LinkedIn</span>
                        </a>
                      )}
                      {mentor.twitter && (
                        <a 
                          href={mentor.twitter.startsWith('http') ? mentor.twitter : `https://twitter.com/${mentor.twitter}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-600 bg-sky-50 hover:bg-sky-100 rounded-lg transition"
                        >
                          <Twitter className="w-3.5 h-3.5 text-sky-500" />
                          <span className="font-medium hidden sm:inline">Twitter</span>
                        </a>
                      )}
                      {mentor.website && (
                        <a 
                          href={mentor.website.startsWith('http') ? mentor.website : `https://${mentor.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-600 bg-green-50 hover:bg-green-100 rounded-lg transition"
                        >
                          <Globe className="w-3.5 h-3.5 text-green-600" />
                          <span className="font-medium hidden sm:inline">Website</span>
                        </a>
                      )}
                      {mentor.facebook && (
                        <a 
                          href={mentor.facebook.startsWith('http') ? mentor.facebook : `https://facebook.com/${mentor.facebook}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-600 bg-blue-600 hover:bg-blue-700 rounded-lg transition"
                        >
                          <Facebook className="w-3.5 h-3.5 text-white" />
                          <span className="font-medium hidden sm:inline text-white">Facebook</span>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="p-6 bg-white border border-gray-200 rounded-2xl">
              <h2 className="mb-3 text-lg font-bold text-gray-900">About</h2>
              <p className="text-sm leading-relaxed text-gray-600">{mentor.bio || "No bio available"}</p>
            </div>

            {/* Expertise */}
            <div className="p-6 bg-white border border-gray-200 rounded-2xl">
              <h2 className="mb-3 text-lg font-bold text-gray-900">Expertise</h2>
              <div className="flex flex-wrap gap-2">
                {mentor.expertise?.map((skill, idx) => (
                  <span key={idx} className="px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-700 rounded-full">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Reviews */}
            <div className="p-6 bg-white border border-gray-200 rounded-2xl">
              <h2 className="mb-4 text-lg font-bold text-gray-900">Reviews</h2>
              {reviews.length === 0 ? (
                <p className="text-sm text-gray-500">No reviews yet</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review._id} className="pb-4 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 overflow-hidden bg-gray-200 rounded-full">
                          {review.student.avatar ? (
                            <img src={review.student.avatar} alt="" className="object-cover w-full h-full" />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full text-xs font-medium text-gray-600">
                              {(review.student?.firstName?.[0] || review.student?.name?.[0] || "S").toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {review.student?.firstName && review.student?.lastName 
                              ? `${review.student.firstName} ${review.student.lastName}` 
                              : review.student?.name || "Student"}
                          </p>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star key={s} className={`w-3 h-3 ${s <= review.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} />
                            ))}
                            <span className="ml-1 text-xs text-gray-400">{formatDate(review.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Booking */}
          <div className="lg:col-span-1">
            <div className="sticky p-6 bg-white border border-gray-200 top-20 rounded-2xl">
              <div className="mb-6 text-center">
                <p className="mb-1 text-xs text-gray-500">Session Price</p>
                <p className="text-3xl font-bold text-gray-900">{fmtXAF(mentor.pricePerSession || 0)}</p>
                <p className="text-xs text-gray-400">per 60 min session</p>
              </div>

              <button
                onClick={() => setShowBooking(true)}
                disabled={!user}
                className="w-full px-4 py-3 font-semibold text-white transition bg-primary rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {user ? "Book a Session" : "Sign in to Book"}
              </button>

              <div className="pt-6 mt-6 border-t border-gray-100">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">What's included:</h3>
                <ul className="space-y-2 text-xs text-gray-600">
                  <li className="flex items-center gap-2">
                    <Video className="w-3.5 h-3.5 text-green-500" />
                    Video call session
                  </li>
                  <li className="flex items-center gap-2">
                    <MessageCircle className="w-3.5 h-3.5 text-green-500" />
                    Chat support after session
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-green-500" />
                    60 minutes per session
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Modal */}
        {showBooking && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowBooking(false)}>
            <div className="w-full max-w-md bg-white shadow-xl rounded-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Book a Session</h3>
                <button onClick={() => setShowBooking(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={bookingForm.scheduledAt}
                    onChange={(e) => setBookingForm((prev) => ({ ...prev, scheduledAt: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Topic *</label>
                  <input
                    type="text"
                    value={bookingForm.topic}
                    onChange={(e) => setBookingForm((prev) => ({ ...prev, topic: e.target.value }))}
                    placeholder="What do you want to discuss?"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Notes (optional)</label>
                  <textarea
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm((prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any specific questions or context..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                <div className="p-3 rounded-lg bg-gray-50">
                  <p className="text-xs text-gray-500">Price: <span className="font-semibold text-gray-900">{fmtXAF(mentor.pricePerSession || 0)}</span></p>
                </div>
              </div>
              <div className="flex gap-2 p-4 border-t border-gray-100">
                <button
                  onClick={handleBookSession}
                  disabled={booking || !bookingForm.scheduledAt || !bookingForm.topic}
                  className="flex-1 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {booking ? <Loader className="w-4 h-4 animate-spin" /> : null}
                  {booking ? "Booking..." : "Confirm Booking"}
                </button>
                <button
                  onClick={() => setShowBooking(false)}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default MentorDetailPage;